import { normalizeRoleKey } from "./roleHelpers.mjs";

const AI_ACCESS_ROLES = new Set(["admin", "miniadmin", "ai-default"]);
const CRM_ENTITY_TYPES = new Set(["company", "person", "lead"]);

const normalizeString = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value).trim();
};

const normalizePositiveId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null;
};

export const readStoredRoleKeys = (storage = globalThis?.localStorage) => {
  const roleSet = new Set();
  if (!storage || typeof storage.getItem !== "function") {
    return [];
  }

  try {
    const rawRoles = JSON.parse(storage.getItem("user-roles") || "[]");
    (Array.isArray(rawRoles) ? rawRoles : []).forEach((entry) => {
      const key = typeof entry === "string" ? entry : entry?.key || entry?.role || entry?.name;
      const normalized = normalizeRoleKey(key);
      if (normalized) {
        roleSet.add(normalized);
      }
    });
  } catch (_) {
    // fallback below
  }

  const legacyRole = normalizeRoleKey(storage.getItem("user-role"));
  if (legacyRole) {
    roleSet.add(legacyRole);
  }

  return Array.from(roleSet.values());
};

export const hasAiWorkspaceAccess = (roleKeys = readStoredRoleKeys()) =>
  (Array.isArray(roleKeys) ? roleKeys : Array.from(roleKeys || [])).some((roleKey) =>
    AI_ACCESS_ROLES.has(normalizeRoleKey(roleKey))
  );

export const buildAiCrmSourcePath = (entityType, entityId) => {
  const normalizedEntityType = normalizeString(entityType).toLowerCase();
  const normalizedEntityId = normalizePositiveId(entityId);
  if (!CRM_ENTITY_TYPES.has(normalizedEntityType) || !normalizedEntityId) {
    return null;
  }

  if (normalizedEntityType === "company") {
    return `/ncrm/companies/${normalizedEntityId}`;
  }
  if (normalizedEntityType === "person") {
    return `/ncrm/people/${normalizedEntityId}`;
  }
  return `/ncrm/leads/${normalizedEntityId}`;
};

export const buildAiWorkspaceTarget = (payload = {}) => {
  const path = normalizeString(payload?.workspace_target?.path) || "/ai-workspace";
  const rawQuery = payload?.workspace_target?.query && typeof payload.workspace_target.query === "object"
    ? payload.workspace_target.query
    : {};
  const query = {};

  Object.entries(rawQuery).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") {
      return;
    }
    query[key] = String(value);
  });

  return { path, query };
};

export const resolveAiLaunchContext = (query = {}) => {
  const source = normalizeString(query?.source).toLowerCase();
  const entityType = normalizeString(query?.entityType).toLowerCase();
  const entityId = normalizePositiveId(query?.entityId);
  const runId = normalizePositiveId(query?.runId);
  const sourcePath = normalizeString(query?.sourcePath) || buildAiCrmSourcePath(entityType, entityId);
  const entityLabel = normalizeString(query?.entityLabel);
  const profileKey = normalizeString(query?.profileKey);

  const isCrmContext = source === "crm";
  if (!isCrmContext || !CRM_ENTITY_TYPES.has(entityType) || !entityId) {
    return {
      active: false,
      source: source || null,
      entityType: entityType || null,
      entityId: entityId || null,
      entityLabel: entityLabel || null,
      profileKey: profileKey || null,
      sourcePath: sourcePath || null,
      runId: runId || null,
      description: "",
    };
  }

  const fallbackEntityLabel = `${entityType.toUpperCase()} #${entityId}`;
  const descriptionParts = [entityLabel || fallbackEntityLabel];
  if (profileKey) {
    descriptionParts.push(`profil ${profileKey}`);
  }
  if (runId) {
    descriptionParts.push(`run #${runId}`);
  }

  return {
    active: true,
    source: "crm",
    entityType,
    entityId,
    entityLabel: entityLabel || fallbackEntityLabel,
    profileKey: profileKey || null,
    sourcePath: sourcePath || null,
    runId: runId || null,
    description: descriptionParts.join(" · "),
  };
};
